import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { withApiState } from '@/utils/withApiState'

export type FeatureFlagStatus = 'ACTIVE' | 'INACTIVE' | 'DEPRECATED'

export interface FeatureFlagItem {
  uuid: string
  code: string
  description: string | null
  status: FeatureFlagStatus
  dateCreated: string
  dateUpdated: string
  assignmentsCount: number
  userCount: number
}

interface FeatureFlagAssignedUser {
  assignmentUuid: string
  userUuid: string
  email: string
  firstName: string | null
  lastName: string | null
  grantedAt: string
  grantedByUserUuid: string | null
  grantedByEmail: string | null
}

export interface FeatureFlagDetail extends FeatureFlagItem {
  users: FeatureFlagAssignedUser[]
}

export interface UserFeatureFlag {
  assignmentUuid: string
  featureFlagUuid: string
  code: string
  description: string | null
  status: FeatureFlagStatus
  grantedAt: string
  grantedByUserUuid: string | null
}

export interface CreateFeatureFlagInput {
  code: string
  description?: string | null
  status?: FeatureFlagStatus
}

export interface UpdateFeatureFlagInput {
  description?: string | null
  status?: FeatureFlagStatus
}

export const useFeatureFlagAdminStore = defineStore('featureFlagAdmin', () => {
  const suiteStore = useSuiteStore()

  const flags = ref<FeatureFlagItem[]>([])
  const isLoading = ref(false)

  async function fetchFlags(): Promise<FeatureFlagItem[]> {
    const result = await withApiState(
      async () => {
        const res = await suiteStore.suiteFetch('feature-flags/admin', {
          method: 'GET',
          credentials: 'include',
        })
        if (res && typeof res !== 'boolean' && res.ok) {
          const json = (await res.json()) as FeatureFlagItem[]
          flags.value = json ?? []
          return flags.value
        }
        return undefined
      },
      { loading: isLoading, logLabel: 'Erreur récupération feature flags' },
    )
    return result ?? []
  }

  async function fetchFlagDetail(uuid: string): Promise<FeatureFlagDetail | null> {
    const res = await suiteStore.suiteFetch(`feature-flags/admin/${uuid}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as FeatureFlagDetail
    }
    return null
  }

  async function createFlag(input: CreateFeatureFlagInput): Promise<{ ok: true; flag: FeatureFlagItem } | { ok: false; message: string }> {
    const res = await suiteStore.suiteFetch('feature-flags/admin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const flag = (await res.json()) as FeatureFlagItem
      flags.value.unshift(flag)
      return { ok: true, flag }
    }
    let message = 'Erreur lors de la création du feature flag'
    if (res && typeof res !== 'boolean') {
      try {
        const body = await res.json()
        if (body?.message) message = Array.isArray(body.message) ? body.message.join(', ') : body.message
      } catch {
        // ignore
      }
    }
    return { ok: false, message }
  }

  async function updateFlag(uuid: string, input: UpdateFeatureFlagInput): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`feature-flags/admin/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const updated = (await res.json()) as FeatureFlagItem
      const idx = flags.value.findIndex((f) => f.uuid === uuid)
      if (idx >= 0) flags.value[idx] = updated
      return true
    }
    return false
  }

  async function deleteFlag(uuid: string): Promise<{ ok: true } | { ok: false; message: string }> {
    const res = await suiteStore.suiteFetch(`feature-flags/admin/${uuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      flags.value = flags.value.filter((f) => f.uuid !== uuid)
      return { ok: true }
    }
    let message = 'Suppression impossible'
    if (res && typeof res !== 'boolean') {
      try {
        const body = await res.json()
        if (body?.message) message = Array.isArray(body.message) ? body.message.join(', ') : body.message
      } catch {
        // ignore
      }
    }
    return { ok: false, message }
  }

  async function fetchFlagsForUser(userUuid: string): Promise<UserFeatureFlag[]> {
    const res = await suiteStore.suiteFetch(`feature-flags/admin/users/${userUuid}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as UserFeatureFlag[]
    }
    return []
  }

  async function assignFlagToUser(featureFlagUuid: string, userUuid: string): Promise<UserFeatureFlag | null> {
    const res = await suiteStore.suiteFetch(`feature-flags/admin/${featureFlagUuid}/users`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userUuid }),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as UserFeatureFlag
    }
    return null
  }

  async function unassignFlagFromUser(featureFlagUuid: string, userUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`feature-flags/admin/${featureFlagUuid}/users/${userUuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    return !!(res && typeof res !== 'boolean' && res.ok)
  }

  return {
    flags,
    isLoading,
    fetchFlags,
    fetchFlagDetail,
    createFlag,
    updateFlag,
    deleteFlag,
    fetchFlagsForUser,
    assignFlagToUser,
    unassignFlagFromUser,
  }
})
